<script setup lang="ts">
import { computed } from 'vue';
import { useThemeVars } from 'naive-ui';

import type { TaskStatus } from '@/api';
import {
  actionResultDetail,
  actionResultTypeLabel,
  actionTypeIcon,
  actionTypeRank,
  groupDisplayName,
  signaturePreparationRequestUid,
  stepStatusTagType,
  type FiredGroupAction,
} from '@/services/playbooks/playbookRunDisplay';
import { gaiaStatus } from '@/components/design/themes/tokens';

// One icon per group-triggered action that ran, hovering to reveal which group
// fired it and what it did. Shared by the runs table column and the run detail's
// group headers so both read the same.
const props = defineProps<{ fired: FiredGroupAction[] }>();

const themeVars = useThemeVars();

const sorted = computed<FiredGroupAction[]>(() =>
  [...props.fired].sort(
    (a, b) => actionTypeRank(a.result.action_type) - actionTypeRank(b.result.action_type)
  )
);

/**
 * Icon tint per status — green done, red failed, violet running, grey otherwise.
 * Running is the Gaia status violet, not `primaryColor` (ink), so a group's
 * running action reads the same as one on an evaluation's own cluster.
 */
function statusColor(status: TaskStatus): string {
  const colorByTagType: Record<string, string> = {
    success: themeVars.value.successColor,
    info: gaiaStatus.running,
    warning: themeVars.value.warningColor,
    error: themeVars.value.errorColor,
  };
  return colorByTagType[stepStatusTagType(status)] ?? themeVars.value.textColor3;
}
</script>

<template>
  <n-flex align="center" :size="4" :wrap="false">
    <n-popover v-for="entry in sorted" :key="entry.result.uid" trigger="hover" placement="top">
      <template #trigger>
        <n-icon :size="16" :color="statusColor(entry.result.status)" style="cursor: default">
          <component :is="actionTypeIcon(entry.result.action_type)" />
        </n-icon>
      </template>
      <n-flex vertical :size="4" style="max-width: 320px">
        <n-text :depth="3" style="font-size: 11px">{{ groupDisplayName(entry.group) }}</n-text>
        <n-flex align="center" :size="6" :wrap="false">
          <span style="font-weight: 600">
            {{ actionResultTypeLabel(entry.result.action_type) }}
          </span>
          <n-tag size="small" :type="stepStatusTagType(entry.result.status)" :bordered="false">
            {{ entry.result.status }}
          </n-tag>
        </n-flex>
        <n-text v-if="actionResultDetail(entry.result)" :depth="3" style="font-size: 12px">
          {{ actionResultDetail(entry.result) }}
        </n-text>
        <!-- Stops propagation so following it doesn't also trigger the row click behind it. -->
        <RouterLink
          v-if="signaturePreparationRequestUid(entry.result)"
          :to="{
            name: 'signing-request-edit',
            params: { signingRequestUid: signaturePreparationRequestUid(entry.result) },
          }"
          style="font-size: 12px"
          @click.stop
        >
          Open signature request
        </RouterLink>
      </n-flex>
    </n-popover>
  </n-flex>
</template>
